import { createAsyncThunk, createSlice, isPending, isRejected } from '@reduxjs/toolkit';
import axios from 'axios';

import { IAdminBooking } from 'app/shared/model/admin-booking.model';
import { serializeAxiosError } from 'app/shared/reducers/reducer.utils';

const initialState = {
  loading: false,
  errorMessage: null as string | null,
  bookings: [] as readonly IAdminBooking[],
  totalItems: 0,
};

export type AdminBookingsState = Readonly<typeof initialState>;

const adminUrl = 'api/admin/bookings';

interface FetchParams {
  page: number;
  size: number;
  sort: string;
  /** RSQL filter (date range, status, free-text via =ik=, …) built on the client. */
  query?: string;
}

// Bookings list via the single /api/admin/bookings/filter?q= endpoint. The client composes the whole RSQL;
// the backend returns an enriched GenericPaginationResponse ({ data, pagination }) — same contract as the other lists.
export const getBookings = createAsyncThunk(
  'adminBookings/fetch_bookings',
  async ({ page, size, sort, query }: FetchParams, { signal }) => {
    const q = query ? `&q=${encodeURIComponent(query)}` : '';
    // Stable PK tiebreaker so rows don't reshuffle across edits when the primary sort has ties (see user reducer).
    const requestUrl = `${adminUrl}/filter?page=${page}&size=${size}&sort=${sort}&sort=id,desc${q}`;
    // signal lets a newer search/filter abort this in-flight request (axios cancels the XHR).
    return axios.get<{ data: IAdminBooking[]; pagination: { totalItems: number } }>(requestUrl, { signal });
  },
  { serializeError: serializeAxiosError },
);

interface UpdateStatusParams {
  id: number;
  status: string;
  /** Required by the backend when status = NO_SHOW. */
  applyStrike?: boolean;
  /** Optional when status = CANCELLED (defaults to CLIENT). */
  cancellationReason?: string;
}

// Admin god-mode status change — any status → any status (SCHEDULED/CONFIRMED/COMPLETED/CANCELLED/
// NO_SHOW). Caller reloads the list on success. undefined fields are dropped by JSON serialization, so the
// backend reads them as null (NO_SHOW then defaults applyStrike=true server-side).
export const updateBookingStatus = createAsyncThunk(
  'adminBookings/update_status',
  async ({ id, status, applyStrike, cancellationReason }: UpdateStatusParams) =>
    axios.patch(`${adminUrl}/${id}/status`, { status, applyStrike, cancellationReason }),
  { serializeError: serializeAxiosError },
);

export const AdminBookingsSlice = createSlice({
  name: 'adminBookings',
  initialState: initialState as AdminBookingsState,
  reducers: {
    reset() {
      return initialState;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getBookings.fulfilled, (state, action) => {
        state.loading = false;
        state.bookings = action.payload.data.data;
        state.totalItems = action.payload.data.pagination.totalItems;
      })
      .addMatcher(isPending(getBookings), state => {
        state.loading = true;
        state.errorMessage = null;
      })
      .addMatcher(isRejected(getBookings), (state, action) => {
        if (action.meta?.aborted) return; // superseded by a newer request — ignore
        state.loading = false;
        state.errorMessage = action.error.message!;
      });
  },
});

export const { reset } = AdminBookingsSlice.actions;

export default AdminBookingsSlice.reducer;
